import _ from 'lodash';
import React, { PureComponent, Fragment } from 'react';
import { connect } from 'react-redux';
import {
  IconButton,
  LinearProgress,
  TextField,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Button,
  InputAdornment,
} from '@mui/material';
import {
  Close,
  ArrowForwardIos,
  KeyboardArrowLeft,
  Mail,
  DeleteOutline,
  Create,
  Error,
} from '@mui/icons-material';
import {
  IStateProps,
  IDispatchProps,
  IProps,
  IState,
} from './interfaces';
import {
  ProgressContainer,
  InputHeader,
  ContactsContentContainer,
  ContactList,
  ListItemHeader,
  TeamContactList,
  StyledTooltip,
  StyledTextField,
  Form,
  FormGroup,
  Label,
  PersonContainer,
  PersonContent,
  PersonAvatar,
  ActionsContainer,
  CancelSaveContainer,
  FeedbackContainer,
} from './styles';
import {
  DialogHeader,
  DialogHeaderTitle,
  DialogContentContainer,
} from '../../../Shared/styles/dialog';
import { WarningContainer } from '../../../Shared/Warning';
import { IAppState } from '../../../../state/reducers';
import {
  initContacts,
  clearAll,
  IContactInfoProcessed,
  TeamPerson,
  validateContact,
  resetCreateContact,
  ValidationKey,
  saveContact,
  deleteContact,
  clearFeedback,
  updateSelected,
} from '../../../../state/exchange/contactInfoDialog';
import ConfirmDialog, { IConfirmDialogResult } from '../../../Shared/ConfirmDialog';

class ContactInfo extends PureComponent<IProps, IState> {
  state: IState = {
    filterText: '',
    deleteConfirmDialogOpen: false,
  };

  componentDidMount() {
    const { onInitContacts } = this.props;
    onInitContacts();
  }

  render() {
    const {
      onExit,
      onValidateContact,
      onResetCreateContact,
      onDeleteContact,
      onSaveContact,
      onClearFeedback,
      onUpdateSelected,
      isLoaded,
      contacts,
      newUser,
      userId,
      userOrgId,
      validationErrors,
      feedback,
      selected,
    } = this.props;

    const {
      filterText,
      deleteConfirmDialogOpen,
    } = this.state;

    const renderBackButton = () => {
      const handleClick = () => {
        onClearFeedback();
        if (selected.team && !selected.person) {
          onUpdateSelected({
            ...selected,
            team: null,
          });
        } else {
          onUpdateSelected({
            ...selected,
            person: null,
          });
        }
      };

      return (
        <Button color="secondary" startIcon={<KeyboardArrowLeft />} onClick={handleClick}>
          Back
        </Button>
      );
    };

    const renderTextFilter = () => {
      const handleTextFilter = (e: any) => {
        if (e.target.value !== filterText) {
          this.setState({
            filterText: e.target.value,
          });
        }
      };

      return (
        <div>
          <InputHeader>Filter Contacts</InputHeader>
          <TextField
            id="outlined-secondary"
            placeholder="Enter team, team code, or contact name"
            variant="outlined"
            fullWidth
            onChange={handleTextFilter}
            value={filterText}
          />
        </div>
      );
    };

    const renderContact = (contactData: IContactInfoProcessed, index: number) => {
      const handleNavigate = () => {
        if (contactData.teamPerson === TeamPerson.person) {
          onUpdateSelected({
            ...selected,
            person: contactData,
          });
        } else {
          onUpdateSelected({
            ...selected,
            team: contactData,
          });
        }
      };
      let intials = '';
      if (contactData.teamPerson === TeamPerson.person) {
        const firstLetter = contactData.title[0];
        const secondLetter = contactData.title.split(' ')[1][0];
        intials = (firstLetter + secondLetter).toUpperCase();
      }

      return (
        <ListItem key={index} button onClick={handleNavigate}>
          <ListItemAvatar>
            <Avatar src={contactData.imgSrc}>{intials}</Avatar>
          </ListItemAvatar>
          <ListItemText primary={contactData.title} secondary={contactData.subtitle} />
          <ListItemSecondaryAction>
            <IconButton onClick={handleNavigate} size="large">
              <ArrowForwardIos color="secondary" />
            </IconButton>
          </ListItemSecondaryAction>
        </ListItem>
      );
    };

    const renderContacts = () => {
      let filteredContacts: IContactInfoProcessed[] = contacts;
      if (filterText !== null) {
        const lowerCaseFilter = filterText.toLowerCase();
        filteredContacts = contacts.filter((contact) => {
          if (contact.title.toLowerCase().includes(lowerCaseFilter)) {
            return true;
          }
          if (contact.teamPerson === TeamPerson.team && contact.subtitle.toLowerCase().includes(lowerCaseFilter)) {
            return true;
          }
          return false;
        });
      }

      if (filteredContacts.length === 0) {
        return (
          <WarningContainer>
            <h1>No contacts found</h1>
          </WarningContainer>
        );
      }

      return (
        <ContactList>
          {filteredContacts.map((item: IContactInfoProcessed, index: number) => (
            <Fragment key={index}>
              {renderContact(item, index)}
              {index !== contacts.length - 1 && (<Divider />)}
            </Fragment>
          ))}
        </ContactList>
      );
    };

    const renderTeam = () => {
      const { team } = selected;
      const peopleOnTeam = contacts.filter((contact) => (contact.teamPerson === TeamPerson.person && team.peopleOnTeam.includes(contact._id)));
      const emails = peopleOnTeam.reduce((accumlator, item) => (accumlator === '' ? item.contactData.email : `${accumlator},${item.contactData.email}`), '');

      return (
        <>
          <ListItemHeader>
            <ListItem>
              <ListItemAvatar>
                <Avatar src={team.imgSrc} />
              </ListItemAvatar>
              <ListItemText primary={team.title} secondary={team.subtitle} />
              <ListItemSecondaryAction>
                <StyledTooltip title={`Send email to: ${_.truncate(emails, { length: 100 })}`} arrow placement="top">
                  <Button
                    color="secondary"
                    variant="contained"
                    startIcon={<Mail />}
                    href={`mailto:${emails}`}
                  >
                    EMAIL ALL
                  </Button>
                </StyledTooltip>
              </ListItemSecondaryAction>
            </ListItem>
          </ListItemHeader>
          <TeamContactList>
            {peopleOnTeam.map((contact: any, index: number) => (
              <Fragment key={index}>
                {renderContact(contact, index)}
                {index !== peopleOnTeam.length - 1 && (
                  <Divider />
                )}
              </Fragment>
            ))}
          </TeamContactList>
        </>
      );
    };

    const renderPerson = () => {
      const { edit, person } = selected;

      const handleSave = () => {
        onClearFeedback();
        onSaveContact(edit);
      };

      const handleCancel = () => {
        onClearFeedback();
        onUpdateSelected({
          ...selected,
          edit: null,
        });
        if (newUser) {
          onResetCreateContact();
        }
      };

      const handleDelete = () => {
        onClearFeedback();
        this.setState({
          deleteConfirmDialogOpen: true,
        });
      };

      const handleEditMode = () => {
        onClearFeedback();
        onUpdateSelected({
          ...selected,
          edit: JSON.parse(JSON.stringify(person)),
        });
      };

      const handleDeleteDialog = async (result: IConfirmDialogResult) => {
        this.setState({
          deleteConfirmDialogOpen: false,
        }, () => {
          if (result.confirmed) {
            onDeleteContact(edit.contactData._id);
          }
        });
      };

      const handleUpdateValue = (value: string, key: ValidationKey) => {
        switch (key) {
          case ValidationKey.Email:
            edit.contactData.email = value;
            break;
          case ValidationKey.Phone:
            edit.contactData.phoneNumber = value;
            break;
          case ValidationKey.FullName:
            edit.contactData.fullName = value;
            break;
          case ValidationKey.JobTitle:
            edit.contactData.jobTitle = value;
            break;
          default:
            break;
        }
        onValidateContact(edit, key);
        onUpdateSelected({
          ...selected,
          edit: { ...edit },
        });
      };

      let intials = '';
      let email = '';
      let phoneNumber = '';
      if (person) {
        const firstLetter = person.title[0];
        const secondLetter = person.title.split(' ')[1][0];
        intials = (firstLetter + secondLetter).toUpperCase();
        ({ contactData: { email } } = person);
        if (person.contactData.phoneNumber) {
          const match = person.contactData.phoneNumber.match(/^(\d{3})(\d{3})(\d{4})$/);
          phoneNumber = `(${match[1]}) ${match[2]}-${match[3]}`;
        }
      }
      if (edit) {
        try {
          const firstLetter = edit.contactData.fullName[0];
          const secondLetter = edit.contactData.fullName.split(' ')[1][0];
          intials = (firstLetter + secondLetter).toUpperCase();
          if (intials.length !== 2) {
            intials = '';
          }
        } catch (e) {
          // Do nothing
        }
      }

      const errorAdornment = {
        endAdornment: (
          <InputAdornment position="end">
            <Error color="error" />
          </InputAdornment>
        ),
      };

      const content = edit ? (
        <PersonContainer>
          <PersonAvatar>
            <Avatar>{intials}</Avatar>
          </PersonAvatar>
          <PersonContent>
            <Form>
              <FormGroup disabled>
                <Label>SCHOOL NAME</Label>
                <StyledTextField
                  value={edit.teamName}
                  helperText="Contact support to update school name"
                  disabled
                  variant="outlined"
                  fullWidth
                />
              </FormGroup>
              <FormGroup>
                <Label>FIRST &amp; LAST NAME *</Label>
                <StyledTextField
                  error={validationErrors.fullName !== ''}
                  helperText={validationErrors.fullName}
                  value={edit.contactData.fullName}
                  variant="outlined"
                  fullWidth
                  onChange={(e: any) => handleUpdateValue(e.target.value, ValidationKey.FullName)}
                  InputProps={validationErrors.fullName !== '' ? {
                    ...errorAdornment,
                  } : {}}
                />
              </FormGroup>
              <FormGroup>
                <Label>JOB TITLE</Label>
                <StyledTextField
                  value={edit.contactData.jobTitle}
                  variant="outlined"
                  fullWidth
                  onChange={(e: any) => handleUpdateValue(e.target.value, ValidationKey.JobTitle)}
                />
              </FormGroup>
              <FormGroup>
                <Label>EMAIL *</Label>
                <StyledTextField
                  error={validationErrors.email !== ''}
                  helperText={validationErrors.email}
                  value={edit.contactData.email}
                  variant="outlined"
                  fullWidth
                  onChange={(e: any) => handleUpdateValue(e.target.value, ValidationKey.Email)}
                  InputProps={validationErrors.email !== '' ? {
                    ...errorAdornment,
                  } : {}}
                />
              </FormGroup>
              <FormGroup>
                <Label>PHONE NUMBER</Label>
                <StyledTextField
                  error={validationErrors.phoneNumber !== ''}
                  helperText={validationErrors.phoneNumber}
                  value={edit.contactData.phoneNumber}
                  variant="outlined"
                  fullWidth
                  onChange={(e: any) => handleUpdateValue(e.target.value, ValidationKey.Phone)}
                  InputProps={validationErrors.phoneNumber !== '' ? {
                    ...errorAdornment,
                  } : {}}
                />
              </FormGroup>
              <ActionsContainer>
                <IconButton
                  color="primary"
                  component="span"
                  onClick={handleDelete}
                  disabled={newUser}
                  size="large"
                >
                  <DeleteOutline fontSize="large" style={newUser ? { display: 'none' } : {}} />
                </IconButton>
                <CancelSaveContainer>
                  <Button variant="outlined" onClick={handleCancel}>CANCEL</Button>
                  <Button variant="contained" color="primary" onClick={handleSave}>SAVE</Button>
                </CancelSaveContainer>
              </ActionsContainer>
            </Form>
          </PersonContent>
          <ConfirmDialog
            open={deleteConfirmDialogOpen}
            title="Confirm delete."
            message="Are you sure you wish to proceed with the delete?"
            onResult={handleDeleteDialog}
          />
        </PersonContainer>
      ) : (
        <PersonContainer>
          <PersonAvatar>
            <Avatar>{intials}</Avatar>
          </PersonAvatar>
          <PersonContent>
            <ListItemHeader>
              <ListItem>
                <ListItemText
                  primary={(
                    <>
                      {person.title}
                      {userOrgId === person.contactData.orgId && (
                        <IconButton
                          style={{ marginLeft: '5px', position: 'absolute', top: '10px' }}
                          onClick={handleEditMode}
                          size="large"
                        >
                          <Create style={{ fontSize: '24px' }} color="primary" />
                        </IconButton>
                      )}
                    </>
                  )}
                  secondary={person.subtitle}
                />
              </ListItem>
            </ListItemHeader>
            <List>
              {(phoneNumber && phoneNumber !== '') && (
                <>
                  <ListItem>
                    <ListItemText primary="PHONE" secondary={phoneNumber} />
                  </ListItem>
                  <Divider />
                </>
              )}
              <ListItem>
                <ListItemText primary="EMAIL" secondary={email} />
                <ListItemSecondaryAction>
                  <StyledTooltip title={`Send email to: ${_.truncate(email, { length: 100 })}`} arrow placement="top">
                    <IconButton href={`mailto:${email}`} size="large">
                      <Mail color="secondary" />
                    </IconButton>
                  </StyledTooltip>
                </ListItemSecondaryAction>
              </ListItem>
            </List>
          </PersonContent>
        </PersonContainer>
      );

      if (feedback) {
        const feedbackDisplay = (
          <FeedbackContainer data-error={feedback.failed}>
            <Error />
            <span>{feedback.message}</span>
          </FeedbackContainer>
        );
        return (
          <>
            {feedbackDisplay}
            {content}
          </>
        );
      }

      return content;
    };

    const renderContent = () => {
      if (selected.edit) {
        return renderPerson();
      } if (selected.team && !selected.person) {
        return renderTeam();
      } if (selected.person) {
        return renderPerson();
      }
      return (
        <>
          {renderTextFilter()}
          {renderContacts()}
        </>
      );
    };

    let modalHeader = 'Contacts';
    if (selected.edit && newUser) {
      modalHeader = 'Create Contact Information';
    } else if (selected.edit) {
      modalHeader = 'Update Contact Information';
    }

    return (
      <>
        <DialogHeader
          data-auto="ContactInfoModal"
        >
          <DialogHeaderTitle style={{ marginLeft: '48px' }}>
            {modalHeader}
          </DialogHeaderTitle>
          <IconButton data-auto="CloseContactInfoModal" onClick={onExit} size="large">
            <Close />
          </IconButton>
        </DialogHeader>
        <ProgressContainer>
          {!isLoaded && <LinearProgress />}
        </ProgressContainer>
        <DialogContentContainer>
          {!selected.edit && (selected.person || selected.team) && (
            renderBackButton()
          )}
          <ContactsContentContainer>
            {(isLoaded || contacts.length > 0) && renderContent()}
            {(!isLoaded && contacts.length === 0) && (
            <WarningContainer>
              <h1>Loading contacts</h1>
            </WarningContainer>
            )}
          </ContactsContentContainer>
        </DialogContentContainer>
      </>
    );
  }
}

const mapStateToProps = (state: IAppState): IStateProps => ({
  isOpen: state.exchange.contactInfoDialog.isOpen,
  isLoaded: state.exchange.contactInfoDialog.isLoaded,
  contacts: state.exchange.contactInfoDialog.contacts,
  newUser: state.exchange.contactInfoDialog.newUser,
  userId: state.exchange.contactInfoDialog.userId,
  userOrgId: state.exchange.contactInfoDialog.userOrgId,
  validationErrors: state.exchange.contactInfoDialog.validationErrors,
  feedback: state.exchange.contactInfoDialog.feedback,
  selected: state.exchange.contactInfoDialog.selected,
});

const mapDispatchToProps: IDispatchProps = {
  onInitContacts: initContacts,
  onExit: clearAll,
  onValidateContact: validateContact,
  onResetCreateContact: resetCreateContact,
  onSaveContact: saveContact,
  onDeleteContact: deleteContact,
  onClearFeedback: clearFeedback,
  onUpdateSelected: updateSelected,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ContactInfo);
